import React, {useMemo} from 'react';

import {useTranslation} from 'react-i18next';

import {DatePicker} from 'components/ui-kit/DatePicker';
import {DateRange} from 'components/ui-kit/DateRange';
import {Select} from 'components/ui-kit/Select';
import {ROUTE} from 'core/routes';
import {
    BILLING_TYPES,
    BillingHistoryAccessors as Accessors,
    BillingInvoicesAccessors as InvoicesAccessors,
    BILLING_INVOICE_STATUS,
} from 'types/billing';
import {mapArrayToI18nOptions} from 'utils/select';

export const useSetTableConfig = (path: {path: string}) => {
    const {t} = useTranslation();
    return useMemo(() => {
        if (path.path === ROUTE.BILLING) {
            return [
                {
                    id: Accessors.id,
                    header: t('pages.profile.billing.fields.id'),
                    width: 50,
                },
                {
                    id: Accessors.documentNumber,
                    header: t('pages.profile.billing.fields.documentNumber'),
                },
                {
                    id: Accessors.amount,
                    header: t('pages.profile.billing.fields.amount'),
                    width: 100,
                    isSortable: true,
                },
                {
                    id: Accessors.operationDate,
                    header: t('pages.profile.billing.fields.date'),
                    width: 80,
                    filter: <DateRange />,
                    isSortable: true,
                },
                {
                    id: Accessors.transactionType,
                    header: t('pages.profile.billing.fields.type'),
                    filter: (
                        <Select
                            isClearable
                            options={mapArrayToI18nOptions(
                                BILLING_TYPES.map((item) => ({key: item, value: item})),
                                'dictionaries.billingStatus',
                            )}
                        />
                    ),
                    width: 50,
                },
                {
                    id: Accessors.action,
                    header: t('pages.profile.billing.fields.action'),
                    width: 30,
                },
            ];
        }
        return [
            {
                id: InvoicesAccessors.irn,
                header: t('pages.profile.billing.invoiceGrid.irn'),
                width: 260,
                isSortable: true,
            },
            {
                id: InvoicesAccessors.tin,
                header: t('pages.profile.billing.invoiceGrid.tin'),
                isSortable: true,
            },
            {
                id: InvoicesAccessors.issueDate,
                header: t('pages.profile.billing.invoiceGrid.issuedDate'),
                width: 170,
                isSortable: true,
                filter: <DatePicker />,
            },
            {
                id: InvoicesAccessors.totalInvoicedQuantity,
                header: t('pages.profile.billing.invoiceGrid.invoiceQuantity'),
                width: 170,
                isSortable: false,
            },
            {
                id: InvoicesAccessors.payableAmount,
                header: t('pages.profile.billing.invoiceGrid.payableAmount'),
                width: 175,
                isSortable: true,
            },
            {
                id: InvoicesAccessors.status,
                header: t('pages.profile.billing.invoiceGrid.paymentStatus'),
                width: 205,
                isSortable: false,
                filter: (
                    <Select
                        isClearable
                        options={mapArrayToI18nOptions(
                            BILLING_INVOICE_STATUS.map((item) => ({key: item, value: item})),
                            'dictionaries.billingInvoiceStatus',
                        )}
                    />
                ),
            },
        ];
    }, [t, path]);
};
